/**
 * Cart JS route — modal, AJAX handlers, qty steppers.
 *
 * Registered in main.js under any router key; the actual bindings guard on
 * `.cart-modal` existing in the DOM (it's injected on every page via app.blade.php).
 *
 * Also runs on the /cart/ page (`.woocommerce-cart` body class) for the inline
 * cart table.
 */

// ── Helpers ────────────────────────────────────────────────────────────────────

const $ = window.jQuery;

function getConfig() {
  return (window.metreelCart || { ajax_url: '/wp-admin/admin-ajax.php', nonce: '' });
}

function isCheckoutPage() {
  return document.body.classList.contains('woocommerce-checkout');
}

// ── Modal open / close ─────────────────────────────────────────────────────────

function openModal() {
  const modal   = document.getElementById('cart-modal');
  const overlay = document.querySelector('.cart-modal-overlay');
  if (!modal) return;

  modal.classList.remove('translate-x-full');
  modal.classList.add('translate-x-0');
  modal.setAttribute('aria-hidden', 'false');
  overlay.classList.add('opacity-50', 'pointer-events-auto');
  overlay.classList.remove('opacity-0', 'pointer-events-none');
  overlay.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';

  // Focus trap: move focus to close button
  const closeBtn = modal.querySelector('.cart-modal__close');
  if (closeBtn) closeBtn.focus();
}

function closeModal() {
  const modal   = document.getElementById('cart-modal');
  const overlay = document.querySelector('.cart-modal-overlay');
  if (!modal) return;

  modal.classList.add('translate-x-full');
  modal.classList.remove('translate-x-0');
  modal.setAttribute('aria-hidden', 'true');
  overlay.classList.remove('opacity-50', 'pointer-events-auto');
  overlay.classList.add('opacity-0', 'pointer-events-none');
  overlay.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
}

// ── Fragment swap ──────────────────────────────────────────────────────────────

/**
 * Replace DOM nodes whose selectors match keys in the fragments map.
 * Also updates any .cart-count badge site-wide.
 *
 * @param {Object} fragments  key = CSS selector, value = HTML string
 * @param {number} cartCount
 */
function applyFragments(fragments, cartCount) {
  if (!fragments) return;

  Object.keys(fragments).forEach(function (selector) {
    var nodes = document.querySelectorAll(selector);
    if (!nodes.length) return;
    var tmp = document.createElement('div');
    tmp.innerHTML = fragments[selector];
    var newNode = tmp.firstElementChild;
    if (!newNode) return;
    nodes.forEach(function (node) {
      node.parentNode.replaceChild(newNode.cloneNode(true), node);
    });
  });

  // Keep all .cart-count badges in sync
  if (typeof cartCount !== 'undefined') {
    document.querySelectorAll('.cart-count').forEach(function (el) {
      el.textContent = cartCount;
      el.classList.toggle('cart-count--empty', cartCount === 0);
    });
    document.querySelectorAll('.cart-modal-count').forEach(function (el) {
      el.textContent = cartCount;
    });
  }

  // Dispatch custom event so other lanes can react
  document.dispatchEvent(new CustomEvent('metreel:cart-updated', {
    detail: { cartCount: cartCount, fragments: fragments },
  }));
}

function applyCartPageLineSubtotals(lineSubtotals) {
  var cartPageForm = document.getElementById('cart-page-form');
  if (!cartPageForm || !lineSubtotals) return;

  var activeKeys = Object.keys(lineSubtotals);

  cartPageForm.querySelectorAll('.cart-page-item[data-key]').forEach(function (row) {
    var key = row.getAttribute('data-key');
    if (activeKeys.indexOf(key) === -1) {
      row.parentNode.removeChild(row);
      return;
    }

    var subtotal = row.querySelector('.cart-subtotal-cell span');
    if (subtotal) {
      subtotal.innerHTML = lineSubtotals[key];
    }
  });
}

// ── Spinner helpers ────────────────────────────────────────────────────────────

function setLoading(btn, loading) {
  if (!btn) return;
  if (loading) {
    btn.disabled = true;
    btn.dataset.originalHtml = btn.innerHTML;
    btn.innerHTML = '<svg class="animate-spin h-4 w-4 inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>';
  } else {
    btn.disabled = false;
    if (btn.dataset.originalHtml) {
      btn.innerHTML = btn.dataset.originalHtml;
    }
  }
}

// ── AJAX request ───────────────────────────────────────────────────────────────

function cartRequest(action, data, successCallback, errorCallback) {
  var cfg = getConfig();
  var payload = Object.assign({ action: action, nonce: cfg.nonce }, data);

  $.ajax({
    url: cfg.ajax_url,
    type: 'POST',
    data: payload,
    success: function (res) {
      if (res && res.success) {
        applyFragments(res.fragments, res.cart_count);
        applyCartPageLineSubtotals(res.line_subtotals);
        // Forward GA4 ecommerce payload (add_to_cart, remove_from_cart) into dataLayer.
        if (res.ga4 && res.ga4.event) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(res.ga4);
        }
        if (successCallback) successCallback(res);
      } else {
        var msg = (res && res.message) ? res.message : 'Something went wrong. Please try again.';
        if (errorCallback) errorCallback(msg);
      }
    },
    error: function () {
      var msg = 'Network error. Please try again.';
      if (errorCallback) errorCallback(msg);
    },
  });
}

// ── Cart-level error display ───────────────────────────────────────────────────

function showCartError(msg) {
  // In modal
  var modalBody = document.querySelector('.cart-modal-body');
  var errorEl   = modalBody && modalBody.querySelector('.cart-modal-error');
  if (!errorEl && modalBody) {
    errorEl = document.createElement('div');
    errorEl.className = 'cart-modal-error mx-4 mt-3 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded';
    modalBody.prepend(errorEl);
  }
  if (errorEl) {
    errorEl.textContent = msg;
    errorEl.style.display = 'block';
  }

  // On cart page
  var pageError = document.querySelector('.cart-page-error');
  if (pageError) {
    pageError.textContent = msg;
    pageError.classList.remove('hidden');
  }
}

// ── Debounce ───────────────────────────────────────────────────────────────────

function debounce(fn, delay) {
  var timer;
  return function () {
    clearTimeout(timer);
    var args = arguments;
    var ctx  = this;
    timer = setTimeout(function () { fn.apply(ctx, args); }, delay);
  };
}

// ── Qty stepper bindings ───────────────────────────────────────────────────────

function bindQtyStepper(root) {
  var debouncedUpdate = debounce(function (key, qty) {
    cartRequest(
      'metreel_cart_update',
      { cart_item_key: key, quantity: qty },
      null,
      showCartError
    );
  }, 300);

  // Plus button
  root.addEventListener('click', function (e) {
    var btn = e.target.closest('.cart-item__qty-plus');
    if (!btn) return;
    var key   = btn.dataset.key;
    var input = root.querySelector('.cart-item__qty-input[data-key="' + key + '"]');
    if (!input) return;
    var newQty = parseInt(input.value, 10) + 1;
    input.value = newQty;
    debouncedUpdate(key, newQty);
  });

  // Minus button
  root.addEventListener('click', function (e) {
    var btn = e.target.closest('.cart-item__qty-minus');
    if (!btn) return;
    var key   = btn.dataset.key;
    var input = root.querySelector('.cart-item__qty-input[data-key="' + key + '"]');
    if (!input) return;
    var newQty = Math.max(0, parseInt(input.value, 10) - 1);
    input.value = newQty;
    debouncedUpdate(key, newQty);
  });

  // Direct input change
  root.addEventListener('change', function (e) {
    var input = e.target.closest('.cart-item__qty-input');
    if (!input) return;
    var key    = input.dataset.key;
    var newQty = Math.max(0, parseInt(input.value, 10) || 0);
    input.value = newQty;
    debouncedUpdate(key, newQty);
  });
}

// ── Remove item binding ────────────────────────────────────────────────────────

function bindRemove(root) {
  root.addEventListener('click', function (e) {
    var btn = e.target.closest('.cart-item__remove');
    if (!btn) return;
    var key = btn.dataset.key;
    cartRequest(
      'metreel_cart_remove',
      { cart_item_key: key },
      null,
      showCartError
    );
  });
}

// ── Add-to-cart intercept ──────────────────────────────────────────────────────

function interceptAddToCart() {
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form.cart');
    if (!form) return;

    // Let non-AJAX scenarios through (e.g. redirect to cart if JS disabled handled by PHP)
    e.preventDefault();

    var productId   = form.querySelector('[name="add-to-cart"]');
    var variationId = form.querySelector('[name="variation_id"]');
    var quantityEl  = form.querySelector('[name="quantity"]');
    var submitBtn   = form.querySelector('[type="submit"]');

    if (!productId) return; // Not a standard add-to-cart form

    setLoading(submitBtn, true);

    cartRequest(
      'metreel_cart_add',
      {
        product_id:   productId.value,
        variation_id: variationId ? variationId.value : 0,
        quantity:     quantityEl  ? parseInt(quantityEl.value, 10) : 1,
      },
      function () {
        setLoading(submitBtn, false);
        // Don't auto-open modal if user is already in checkout
        if (!isCheckoutPage()) {
          openModal();
        }
      },
      function (msg) {
        setLoading(submitBtn, false);
        showCartError(msg);
        if (!isCheckoutPage()) {
          openModal();
        }
      }
    );
  });
}

// ── Coupon form bindings (cart page) ──────────────────────────────────────────

function bindCouponForm() {
  var applyBtn = document.getElementById('cart-coupon-apply');
  if (!applyBtn) return;

  // On checkout the metreel fragments don't cover the review-order table or
  // payment block — ask WC to re-render both after any coupon change.
  function refreshCheckout() {
    if (isCheckoutPage() && $) {
      $(document.body).trigger('update_checkout');
    }
  }

  function applyCoupon() {
    var input = document.getElementById('cart-coupon-input');
    var msgEl = document.querySelector('.cart-coupon-msg');
    if (!input || !input.value.trim()) return;

    setLoading(applyBtn, true);

    cartRequest(
      'metreel_cart_apply_coupon',
      { coupon_code: input.value.trim() },
      function (res) {
        setLoading(applyBtn, false);
        if (msgEl) {
          msgEl.textContent = res.message || 'Coupon applied.';
          msgEl.classList.remove('hidden', 'text-red-600');
          msgEl.classList.add('text-green-600');
        }
        input.value = '';
        refreshCheckout();
      },
      function (msg) {
        setLoading(applyBtn, false);
        if (msgEl) {
          msgEl.textContent = msg;
          msgEl.classList.remove('hidden', 'text-green-600');
          msgEl.classList.add('text-red-600');
        }
      }
    );
  }

  applyBtn.addEventListener('click', applyCoupon);

  // Enter in the input applies the coupon; on checkout it would otherwise
  // submit the surrounding checkout form.
  var couponInput = document.getElementById('cart-coupon-input');
  if (couponInput) {
    couponInput.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      applyCoupon();
    });
  }

  // Remove coupon buttons
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.cart-coupon-remove');
    if (!btn) return;
    var code = btn.dataset.coupon;
    cartRequest(
      'metreel_cart_remove_coupon',
      { coupon_code: code },
      function () {
        hideCouponMsg();
        refreshCheckout();
      },
      showCartError
    );
  });

  function hideCouponMsg() {
    var msgEl = document.querySelector('.cart-coupon-msg');
    if (msgEl) {
      msgEl.classList.add('hidden');
    }
  }

  // On checkout, coupons are removed via WC's native [Remove] link in the
  // review table (wc-checkout.js) — clear our stale "applied" message when
  // that happens.
  if ($) {
    $(document.body).on('removed_coupon removed_coupon_in_checkout', hideCouponMsg);
  }
}

// ── Focus trap ────────────────────────────────────────────────────────────────

function bindFocusTrap() {
  var modal = document.getElementById('cart-modal');
  if (!modal) return;

  modal.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var focusable = modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    var first = focusable[0];
    var last  = focusable[focusable.length - 1];
    if (!first) return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });
}

// ── Main route export ──────────────────────────────────────────────────────────

export default {
  init() {
    var modal = document.getElementById('cart-modal');

    // ── Trigger open ──────────────────────────────────────────────────────
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-cart-trigger="modal"]');
      if (!trigger) return;
      e.preventDefault();
      openModal();
    });

    // ── Close: button ──────────────────────────────────────────────────────
    document.addEventListener('click', function (e) {
      if (e.target.closest('.cart-modal__close')) {
        closeModal();
      }
    });

    // ── Close: overlay ─────────────────────────────────────────────────────
    var overlay = document.querySelector('.cart-modal-overlay');
    if (overlay) {
      overlay.addEventListener('click', closeModal);
    }

    // ── Close: ESC key ─────────────────────────────────────────────────────
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal();
    });

    // ── Qty steppers & remove inside modal ────────────────────────────────
    if (modal) {
      bindQtyStepper(modal);
      bindRemove(modal);
      bindFocusTrap();
    }

    // ── Qty steppers & remove on cart page ────────────────────────────────
    var cartPageForm = document.getElementById('cart-page-form');
    if (cartPageForm) {
      bindQtyStepper(cartPageForm);
      bindRemove(cartPageForm);
    }

    // ── Add-to-cart intercept (global, all pages) ──────────────────────────
    interceptAddToCart();

    // ── WC native AJAX add-to-cart hook (archive + related-products cards) ──
    // The PDP uses our `form.cart` submit intercept above; archive cards are
    // anchor links handled by WC's bundled `wc-add-to-cart.js`. WC fires
    // `added_to_cart` on `document.body` after its own AJAX returns, by which
    // time our `.cart-modal-body` and `.cart-modal-footer` fragments have
    // already swapped in (registered in `app/woocommerce.php`). All we need
    // to do here is pop the drawer open so the user sees the new item.
    // Re-bind the qty/remove steppers afterwards because fragment-replaceWith
    // wipes the old DOM nodes and their listeners.
    if (window.jQuery) {
      window.jQuery(document.body).on('added_to_cart', function () {
        var refreshedModal = document.getElementById('cart-modal');
        if (refreshedModal) {
          bindQtyStepper(refreshedModal);
          bindRemove(refreshedModal);
        }
        if (!isCheckoutPage()) {
          openModal();
        }
      });
    }

    // ── Coupon form (cart page) ────────────────────────────────────────────
    bindCouponForm();
  },

  finalize() {
    // No finalize logic required currently.
  },
};



// WEBPACK FOOTER //
// ./scripts/routes/cart.js